import React, { useEffect, useState } from 'react';
import styles from './BonusVideos.module.scss';
import type { ITraining } from '../../../types';
import { BonusVideosDialog } from '../../shared/VideoDialog';
import VideoPreviewDialog from '../../shared/VideoPreviewDialog';
import VideoCard from '../../shared/VideoCard';
import Loader from '../../shared/Loader';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '../../icons';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { useBonusVideos } from '../../../redux/hooks/useBonusVideos';
import { useVideoProcessingStatus } from '../../../hooks/useVideoProcessingStatus';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableVideoCard = ({
    video,
    index,
    onEdit,
    onDelete,
    onClick,
    isMobile,
    onStatusUpdate,
    isNewVideo
}: {
    video: ITraining;
    index: number;
    onEdit: (video: ITraining) => void;
    onDelete: (id: number) => void;
    onClick: (video: ITraining) => void;
    isMobile: boolean;
    onStatusUpdate?: (videoId: number, updates: Partial<ITraining>) => void;
    isNewVideo?: boolean;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: video.id.toString(),
        disabled: isMobile
    });

    // Хук для отслеживания статуса обработки видео
    const { status: processingStatus, message: processingMessage } = useVideoProcessingStatus({
        videoId: video.id,
        autoRefresh: true,
        refreshInterval: 3000,
        onStatusChange: (status, data) => {
            // Обновляем видео в Redux при получении новых данных
            if (onStatusUpdate && data) {
                const updates: Partial<ITraining> = {};

                // Обновляем video_url если получили
                if (data.url && data.url !== (typeof video.video_url === 'string' ? video.video_url : '')) {
                    (updates as any).video_url = data.url;
                }

                // Обновляем preview_url если получили
                if (data.preview_url && data.preview_url !== (typeof video.preview_url === 'string' ? video.preview_url : '')) {
                    (updates as any).preview_url = data.preview_url;
                }

                // Обновляем title если получили
                if (data.title && data.title !== video.title) {
                    updates.title = data.title;
                }

                // Если есть обновления, применяем их
                if (Object.keys(updates).length > 0) {
                    onStatusUpdate(video.id, updates);
                }
            }
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${styles.draggableItem} ${isDragging ? styles.isDragging : ''} ${styles.draggable}`}
            {...(!isMobile ? { ...attributes, ...listeners } : {})}
        >
            <VideoCard
                id={video.id}
                title={video.title}
                description={video.description}
                thumbnailUrl={typeof video.preview_url === 'string' ? video.preview_url : ''}
                videoUrl={typeof video.video_url === 'string' ? video.video_url : ''}
                processingStatus={processingStatus}
                processingMessage={processingMessage}
                onEdit={() => onEdit(video)}
                onDelete={() => onDelete(video.id)}
                onClick={() => onClick(video)}
                horizontal={true}
                hideTitle={true}
                showProcessingIndicator={processingStatus !== 'completed'}
                isNewVideo={isNewVideo}
            />
            {(video.title || video.description) && (
                <div className={styles.descriptionContainer}>
                    {video.title && <h3 className={styles.descriptionTitle}>{video.title}</h3>}
                    {video.description && <p className={styles.descriptionText}>{video.description}</p>}
                </div>
            )}
        </div>
    );
};

const BonusVideos: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [activeVideo, setActiveVideo] = useState<ITraining | null>(null);
    const [newVideoIds, setNewVideoIds] = useState<Set<number>>(new Set());

    const {
        videos,
        selectedVideo,
        editingVideo,
        isLoading,
        isSaving,
        deleteDialog,
        openDialog,
        language,
        hasOrderChanges,
        loadBonusVideos,
        handleSubmit,
        handleEdit,
        handleDelete,
        confirmDelete,
        closeDialog,
        selectVideo,
        closeVideoPreview,
        handleSaveAll,
        openAddDialog,
        closeDeleteDialog,
        reorderVideos,
        updateVideoFromStatus
    } = useBonusVideos();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 600);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (language && i18n.language !== language) {
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    useEffect(() => {
        loadBonusVideos();
    }, [loadBonusVideos]);

    // Определяем, есть ли видео для отображения
    const hasVideos = videos.length > 0;

    // Обработка начала перетаскивания
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const id = active.id.toString();
        const video = videos.find(v => v.id.toString() === id) || null;
        setActiveVideo(video);
    };

    // Обработка окончания перетаскивания
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        setActiveVideo(null);

        if (!over) return;

        const activeId = active.id.toString();
        const overId = over.id.toString();

        if (activeId === overId) return;

        // Вызов redux-действия для изменения порядка
        reorderVideos(activeId, overId);
    };

    // Обертка для handleSubmit с отслеживанием новых видео
    const handleVideoSubmit = async (data: any) => {
        try {
            const result = await handleSubmit(data);

            // Если добавление прошло успешно и мы получили ID нового видео
            if (result && typeof result === 'object' && 'type' in result && result.type.endsWith('/fulfilled') && 'payload' in result && result.payload && typeof result.payload === 'object' && 'id' in result.payload) {
                const videoId = (result.payload as any).id;
                setNewVideoIds(prev => new Set(prev).add(videoId));

                // Убираем ID из списка новых через некоторое время
                setTimeout(() => {
                    setNewVideoIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                }, 30000); // 30 секунд на обработку
            }
        } catch (error) {
            console.error('Ошибка при добавлении бонусного видео:', error);
        }
    };

    // Callback для обновления видео при получении новых данных
    const handleVideoReady = (videoId: number) => {
        setNewVideoIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoId);
            return newSet;
        });
    };

    // Обертка для updateVideoFromStatus с проверкой готовности
    const handleStatusUpdate = (videoId: number, updates: Partial<ITraining>) => {
        updateVideoFromStatus(videoId, updates);

        // Если получили video_url или preview_url, убираем видео из списка новых
        if ((updates.video_url || updates.preview_url)) {
            handleVideoReady(videoId);
        }
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>
                    {t('content.bonus.title')}
                </h1>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={openAddDialog}
                    >
                        <AddIcon /> {t('content.bonus.addButton')}
                    </button>
                    <button
                        className={`${styles.button} ${styles.buttonSuccess} ${isSaving ? styles.buttonDisabled : ''} ${!hasOrderChanges ? styles.buttonDisabled : ''}`}
                        onClick={handleSaveAll}
                        disabled={isSaving || !hasOrderChanges}
                    >
                        {isSaving ? (
                            <span className={styles.spinnerContainer}>
                                <span className={styles.spinner}></span>
                            </span>
                        ) : t('common.save')}
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className={styles.emptyState}>
                    <Loader fullScreen={false} />
                </div>
            ) : hasVideos ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={videos.map(v => v.id.toString())}
                        strategy={rectSortingStrategy}
                    >
                        <div className={styles.grid}>
                            {(videos.length > 1 ? [...videos].sort((a, b) => a.order - b.order) : videos).map((video: ITraining, index: number) => (
                                <SortableVideoCard
                                    key={video.id}
                                    video={video}
                                    index={index}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onClick={selectVideo}
                                    isMobile={isMobile}
                                    onStatusUpdate={handleStatusUpdate}
                                    isNewVideo={newVideoIds.has(video.id)}
                                />
                            ))}
                        </div>
                    </SortableContext>

                    <DragOverlay dropAnimation={{
                        sideEffects: defaultDropAnimationSideEffects({
                            styles: {
                                active: {
                                    opacity: '0.5'
                                }
                            }
                        })
                    }}>
                        {activeVideo && (
                            <div className={`${styles.draggableItem} ${styles.isDragging}`}>
                                <VideoCard
                                    id={activeVideo.id}
                                    title={activeVideo.title}
                                    description={activeVideo.description}
                                    thumbnailUrl={typeof activeVideo.preview_url === 'string' ? activeVideo.preview_url : ''}
                                    videoUrl={typeof activeVideo.video_url === 'string' ? activeVideo.video_url : ''}
                                    onEdit={() => { }}
                                    onDelete={() => { }}
                                    onClick={() => { }}
                                    horizontal={true}
                                    hideTitle={true}
                                />
                                {(activeVideo.title || activeVideo.description) && (
                                    <div className={styles.descriptionContainer}>
                                        {activeVideo.title && <h3 className={styles.descriptionTitle}>{activeVideo.title}</h3>}
                                        {activeVideo.description && <p className={styles.descriptionText}>{activeVideo.description}</p>}
                                    </div>
                                )}
                            </div>
                        )}
                    </DragOverlay>
                </DndContext>
            ) : (
                <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>{t('content.bonus.noVideos')}</p>
                </div>
            )}

            <BonusVideosDialog
                open={openDialog}
                onClose={closeDialog}
                onSubmit={handleVideoSubmit}
                dialogTitle={editingVideo ? t('content.bonus.editTitle') : t('content.bonus.addTitle')}
                initialData={editingVideo ? {
                    id: editingVideo.id,
                    title: editingVideo.title,
                    description: editingVideo.description || '',
                    videoType: editingVideo.video_type as 'youtube' | 'file',
                    youtubeUrl: editingVideo.youtube_url,
                    language: editingVideo.language as 'ru' | 'it',
                } : undefined}
            />

            <VideoPreviewDialog
                open={!!selectedVideo}
                onClose={closeVideoPreview}
                title={selectedVideo?.title || ''}
                videoUrl={selectedVideo?.youtube_url || (typeof selectedVideo?.video_url === 'string' ? selectedVideo.video_url : '')}
                isYouTube={selectedVideo?.video_type === 'youtube'}
            />

            <ConfirmDialog
                open={deleteDialog.open}
                title={t('content.bonus.deleteDialog.title')}
                message={t('content.bonus.deleteDialog.message', { title: deleteDialog.title })}
                onConfirm={confirmDelete}
                onCancel={closeDeleteDialog}
            />
        </div>
    );
};

export default BonusVideos;